import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import "./Navbar.css";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About" },
  { to: "/partner", label: "Partner" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { currentUser, userProfile, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const dropdownRef = useRef(null);

  const cartCount = cart.reduce((n, i) => n + (i.quantity || 1), 0);

  useEffect(() => {
    let ticking = false;
    function handleScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          if (currentY < 100) {
            setHidden(false);
          } else if (currentY > lastScrollY.current) {
            setHidden(true);
            setDropdownOpen(false);
          } else if (currentY < lastScrollY.current - 10) {
            setHidden(false);
          }
          lastScrollY.current = currentY;
          ticking = false;
        });
        ticking = true;
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  async function handleLogout() {
    try { await logout(); navigate("/login"); } catch (e) {}
  }

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <style>{`
        body { padding-top: 76px; }
        @media (max-width: 860px) { body { padding-top: 64px; } }

      .pj-navbar-wrap {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 9999;
          display: flex;
          justify-content: center;
          padding: 16px 24px;
          pointer-events: none;
          transition: transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.8s ease;
          will-change: transform, opacity;
        }
        .pj-navbar-wrap.hidden {
          transform: translateY(-140%);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease;
        }
        body { padding-top: 88px; }
        @media (max-width: 860px) { body { padding-top: 72px; } }
        .pj-navbar {
          display: flex;
          align-items: center;
          gap: 2px;
          background: #2D5016;
          border-radius: 100px;
          padding: 4px;
          box-shadow: 0 6px 24px rgba(45,80,22,0.25), 0 2px 8px rgba(0,0,0,0.1);
          pointer-events: all;
          max-width: 720px;
          width: 100%;
        }
        .pj-nav-logo {
          display: flex; align-items: center; gap: 8px;
          padding-right: 8px; text-decoration: none; flex-shrink: 0;
          min-width: 0;
        }
        .pj-nav-logo-img {
          width: 36px; height: 36px; border-radius: 50%; background: #fff;
          display: flex; align-items: center; justify-content: center;
          overflow: visible; flex-shrink: 0;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .pj-nav-logo:hover .pj-nav-logo-img {
          transform: scale(1.4);
          box-shadow: 0 6px 20px rgba(45,80,22,0.3);
          z-index: 10;
          position: relative;
        }
        .pj-nav-logo:hover .pj-nav-logo-name { color: #C8860A; }
        .pj-nav-logo img { width: 30px; height: 30px; object-fit: contain; }
        .pj-nav-logo-text { display: flex; flex-direction: column; line-height: 1.2; }
        .pj-nav-logo-name { font-family: 'Playfair Display', serif; font-size: 0.88rem; font-weight: 700; color: #F7F0E3; }
        .pj-nav-logo-tag { font-size: 0.55rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #C8860A; }

        .pj-nav-links {
          display: flex; align-items: center;
          flex: 1; justify-content: center; gap: 2px; padding: 0 4px;
        }
        .pj-nav-link {
          position: relative; padding: 6px 10px; border-radius: 100px;
          text-decoration: none; font-size: 0.78rem; font-weight: 500;
          color: #C8860A; font-family: 'Inter', sans-serif; overflow: hidden;
          transition: color 0.2s, background 0.2s; letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .pj-nav-link.active { background: rgba(255,255,255,0.12); color: #F7F0E3; }
        .pj-nav-link-inner { display: block; position: relative; overflow: hidden; height: 1.2em; text-align: center; }
        .pj-nav-link-text { display: block; transition: transform 0.3s cubic-bezier(0.22,1,0.36,1); line-height: 1.2; }
        .pj-nav-link-text.clone { position: absolute; top: 100%; left: 0; right: 0; color: #F7F0E3; font-weight: 600; text-align: center; }
        .pj-nav-link:hover .pj-nav-link-text:not(.clone) { transform: translateY(-100%); }
        .pj-nav-link:hover .pj-nav-link-text.clone { transform: translateY(-100%); }
        .pj-nav-link:hover { color: #F7F0E3; background: rgba(255,255,255,0.1); }

        .pj-nav-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }

        .pj-nav-cart {
          display: flex; align-items: center; justify-content: center;
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(255,255,255,0.1); color: #F7F0E3;
          text-decoration: none; position: relative; transition: background 0.2s;
        }
        .pj-nav-cart:hover { background: rgba(255,255,255,0.2); }
        .pj-cart-badge {
          position: absolute; top: 4px; right: 4px;
          background: #C8860A; color: #fff; font-size: 0.55rem; font-weight: 700;
          width: 16px; height: 16px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }
        .pj-nav-profile-btn {
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(255,255,255,0.15); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #F7F0E3; font-size: 0.88rem; font-weight: 700;
          font-family: 'Playfair Display', serif; transition: background 0.2s;
        }
        .pj-nav-profile-btn:hover { background: rgba(255,255,255,0.25); }
        .pj-nav-signin {
          background: #F7F0E3; color: #2D5016; border: none; border-radius: 100px;
          padding: 6px 14px; font-size: 0.74rem;font-weight: 700; cursor: pointer;
          font-family: 'Inter', sans-serif; text-decoration: none;
          display: flex; align-items: center; transition: background 0.2s;
        }
        .pj-nav-signin:hover { background: #ede6d6; }

        .pj-profile-menu { position: relative; }
        .pj-profile-dropdown {
          position: absolute; top: calc(100% + 10px); right: 0;
          background: #fff; border: 1px solid #E8DCC4; border-radius: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12); min-width: 180px;
          overflow: hidden; z-index: 99999;
        }
        .pj-dropdown-item {
          display: block; padding: 12px 18px; font-size: 0.85rem; color: #2D5016;
          text-decoration: none; font-family: 'Inter', sans-serif;
          border-bottom: 1px solid #f0ece4; transition: background 0.15s;
          cursor: pointer; background: none; border-left: none;
          border-right: none; border-top: none; width: 100%; text-align: left;
        }
        .pj-dropdown-item:last-child { border-bottom: none; }
        .pj-dropdown-item:hover { background: #F7F0E3; }
        .pj-dropdown-item.logout { color: #c0392b; }

       .pj-hamburger {
          display: none; flex-direction: column; gap: 4px;
          background: rgba(255,255,255,0.1); border: none; cursor: pointer;
          border-radius: 50%; width: 36px; height: 36px;
          align-items: center; justify-content: center;
        }
        .pj-hamburger span { display: block; width: 18px; height: 2px; background: #F7F0E3; border-radius: 2px; }

        .pj-mobile-menu {
          position: fixed; inset: 0; z-index: 999998;
          background: linear-gradient(160deg, #0a1f05 0%, #1a3a0a 40%, #2D5016 100%);
          display: flex; flex-direction: column;
          animation: menuFadeIn 0.3s ease;
        }
        @keyframes menuFadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .pj-mobile-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 24px 28px; border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .pj-mobile-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; }
        .pj-mobile-brand-img {
          width: 44px; height: 44px; border-radius: 50%; background: #fff;
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .pj-mobile-brand-img img { width: 36px; height: 36px; object-fit: contain; }
        .pj-mobile-brand-name { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 700; color: #F7F0E3; display: block; }
        .pj-mobile-brand-tag { font-size: 0.6rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: #C8860A; display: block; }
        .pj-mobile-close {
          background: rgba(255,255,255,0.1); border: none; color: #F7F0E3;
          width: 40px; height: 40px; border-radius: 50%; font-size: 1rem;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .pj-mobile-links { flex: 1; padding: 32px 28px; display: flex; flex-direction: column; gap: 4px; overflow-y: auto; }
        .pj-mobile-nav-link {
          display: block; padding: 14px 0; text-decoration: none;
          font-family: 'Playfair Display', serif; font-size: 1.4rem;
          font-weight: 600; color: rgba(247,240,227,0.7);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          transition: color 0.2s, padding-left 0.2s; letter-spacing: 0.01em;
        }
        .pj-mobile-nav-link:hover, .pj-mobile-nav-link.active { color: #F7F0E3; padding-left: 8px; }
        .pj-mobile-nav-link.active { color: #C8860A; }
        .pj-mobile-divider { height: 1px; background: rgba(255,255,255,0.08); margin: 16px 0; }
        .pj-mobile-account-link {
          display: block; padding: 10px 0; text-decoration: none;
          font-family: 'Inter', sans-serif; font-size: 0.9rem;
          color: rgba(247,240,227,0.55); transition: color 0.2s;
          background: none; border: none; text-align: left; cursor: pointer; width: 100%;
        }
        .pj-mobile-account-link:hover { color: #F7F0E3; }
        .pj-mobile-footer { padding: 20px 28px; border-top: 1px solid rgba(255,255,255,0.08); }
        .pj-mobile-cart-btn {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          width: 100%; padding: 14px;
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15);
          border-radius: 12px; color: #F7F0E3; text-decoration: none;
          font-family: 'Inter', sans-serif; font-size: 0.9rem; font-weight: 600;
          transition: background 0.2s;
        }
        .pj-mobile-cart-btn:hover { background: rgba(255,255,255,0.18); }

        @media (max-width: 860px) {
          .pj-navbar-wrap {
            padding: 12px 10px;
            width: 100%;
            box-sizing: border-box;
          }

          .pj-navbar {
            width: 100%;
            max-width: none;
            box-sizing: border-box;
            border-radius: 100px;
            padding: 6px 8px;
            gap: 0;
          }

          .pj-nav-logo {
            flex: 1 1 auto;
            min-width: 0;
            padding-right: 4px;
          }

          .pj-nav-logo-text {
            min-width: 0;
          }

          .pj-nav-logo-name {
            font-size: 0.88rem;
          }

          .pj-nav-logo-tag {
            font-size: 0.48rem;
          }

          .pj-nav-links {
            display: none;
          }

          .pj-nav-actions {
            flex: 0 0 auto;
            margin-left: auto;
            gap: 4px;
          }

          .pj-hamburger {
            display: flex;
          }
        }

        /* =====================================================
           FINAL MOBILE NAVBAR ALIGNMENT
           ===================================================== */

        @media (max-width: 860px) {

          .pj-navbar-wrap {
            width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            padding: 12px 20px !important;
            box-sizing: border-box !important;
          }

          .pj-navbar {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            height: 64px !important;
            padding: 6px 8px 6px 10px !important;
            box-sizing: border-box !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 0 !important;
          }

          /* Logo stays on the LEFT */
          .pj-nav-logo {
            flex: 1 1 auto !important;
            width: auto !important;
            max-width: none !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 8px 0 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 8px !important;
          }

          .pj-nav-logo-img {
            width: 42px !important;
            height: 42px !important;
            flex: 0 0 42px !important;
          }

          .pj-nav-logo img {
            width: 34px !important;
            height: 34px !important;
          }

          .pj-nav-logo-text {
            min-width: 0 !important;
            flex: 0 1 auto !important;
          }

          .pj-nav-logo-name {
            font-size: 1.12rem !important;
            white-space: nowrap !important;
          }

          .pj-nav-logo-tag {
            font-size: 0.50rem !important;
            letter-spacing: 0.12em !important;
            white-space: nowrap !important;
          }

          /* Actions stay together on the RIGHT */
          .pj-nav-actions {
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 8px !important;
            overflow: visible !important;
          }

          .pj-nav-cart,
          .pj-nav-profile-btn,
          .pj-hamburger {
            width: 42px !important;
            height: 42px !important;
            flex: 0 0 42px !important;
          }

          .pj-nav-cart svg {
            width: 21px !important;
            height: 21px !important;
          }

          .pj-nav-signin {
            height: 42px !important;
            min-width: 92px !important;
            padding: 0 16px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            white-space: nowrap !important;
            box-sizing: border-box !important;
          }

          .pj-hamburger {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            gap: 4px !important;
          }

          .pj-hamburger span {
            width: 19px !important;
            height: 2px !important;
          }
        }

        /* SMALL PHONES */
        @media (max-width: 480px) {

          .pj-navbar-wrap {
            padding-left: 14px !important;
            padding-right: 14px !important;
          }

          .pj-navbar {
            padding-left: 8px !important;
            padding-right: 6px !important;
          }

          .pj-nav-logo {
            gap: 7px !important;
            padding-right: 5px !important;
          }

          .pj-nav-logo-img {
            width: 40px !important;
            height: 40px !important;
            flex-basis: 40px !important;
          }

          .pj-nav-logo img {
            width: 32px !important;
            height: 32px !important;
          }

          .pj-nav-logo-name {
            font-size: 1.05rem !important;
          }

          .pj-nav-logo-tag {
            font-size: 0.46rem !important;
          }

          .pj-nav-actions {
            gap: 6px !important;
          }

          .pj-nav-cart,
          .pj-nav-profile-btn,
          .pj-hamburger {
            width: 40px !important;
            height: 40px !important;
            flex-basis: 40px !important;
          }

          .pj-nav-signin {
            min-width: 84px !important;
            height: 40px !important;
            padding: 0 13px !important;
            font-size: 0.74rem !important;
          }
        }

      `}</style>

      <div className={`pj-navbar-wrap${hidden ? " hidden" : ""}`}>
        <nav className="pj-navbar">
          <Link to="/" className="pj-nav-logo">
            <div className="pj-nav-logo-img">
              <img src="/logo.png" alt="PureJar" />
            </div>
            <div className="pj-nav-logo-text">
              <span className="pj-nav-logo-name">PureJar</span>
              <span className="pj-nav-logo-tag">Proudly Pahadi</span>
            </div>
          </Link>

          <div className="pj-nav-links">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={`pj-nav-link ${isActive(to) ? "active" : ""}`}>
                <span className="pj-nav-link-inner">
                  <span className="pj-nav-link-text">{label}</span>
                  <span className="pj-nav-link-text clone">{label}</span>
                </span>
              </Link>
            ))}
          </div>

          <div className="pj-nav-actions">

            {/* Cart */}
            <Link to="/cart" className="pj-nav-cart" aria-label="Cart">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {cartCount > 0 && <span className="pj-cart-badge">{cartCount}</span>}
            </Link>

            {/* Profile */}
            {currentUser ? (
              <div className="pj-profile-menu" ref={dropdownRef}>
                <button className="pj-nav-profile-btn" onClick={() => setDropdownOpen(v => !v)}>
                  {(userProfile?.name || currentUser.email || "U")[0].toUpperCase()}
                </button>
                {dropdownOpen && (
                  <div className="pj-profile-dropdown">
                    <Link to="/profile" className="pj-dropdown-item" onClick={() => setDropdownOpen(false)}>My Profile</Link>
                    <Link to="/orders" className="pj-dropdown-item" onClick={() => setDropdownOpen(false)}>My Orders</Link>
                    <button onClick={handleLogout} className="pj-dropdown-item logout">Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="pj-nav-signin">Sign In</Link>
            )}

            {/* Hamburger */}
            <button className="pj-hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu">
              <span /><span /><span />
            </button>
          </div>
        </nav>
      </div>

      {/* Full screen mobile menu */}
      {menuOpen && (
        <div className="pj-mobile-menu">
          <div className="pj-mobile-header">
            <div className="pj-mobile-brand">
              <div className="pj-mobile-brand-img">
                <img src="/logo.png" alt="PureJar" />
              </div>
              <div>
                <span className="pj-mobile-brand-name">PureJar</span>
                <span className="pj-mobile-brand-tag">Proudly Pahadi</span>
              </div>
            </div>
            <button className="pj-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
          </div>

          <div className="pj-mobile-links">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to} to={to}
                className={`pj-mobile-nav-link ${isActive(to) ? "active" : ""}`}
                onClick={() => { setMenuOpen(false); window.scrollTo(0,0); }}
              >{label}</Link>
            ))}
            <div className="pj-mobile-divider" />
            {currentUser ? (
              <>
                <Link to="/profile" className="pj-mobile-account-link" onClick={() => { setMenuOpen(false); window.scrollTo(0,0); }}>My Profile</Link>
                <Link to="/orders" className="pj-mobile-account-link" onClick={() => { setMenuOpen(false); window.scrollTo(0,0); }}>My Orders</Link>
                <button className="pj-mobile-account-link" onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ color: "#ff9999" }}>Sign Out</button>
              </>
            ) : (
              <Link to="/login" className="pj-mobile-account-link" onClick={() => setMenuOpen(false)}>Sign In</Link>
            )}
          </div>

          <div className="pj-mobile-footer">
            <Link to="/cart" className="pj-mobile-cart-btn" onClick={() => setMenuOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {cartCount > 0 ? `View Cart (${cartCount})` : "View Cart"}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}